import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Facebook,
  Instagram,
  Linkedin,
  Menu,
  Search,
  X,
  Youtube,
  Leaf,
} from "lucide-react";
import { NAV } from "../mock";

export default function Navbar() {  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
  }, [pathname]);

  const solid = scrolled || open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-white/90 shadow-[0_8px_30px_rgba(24,40,32,0.08)] backdrop-blur-xl"
          : "bg-gradient-to-b from-black/45 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[74px] max-w-[1400px] items-center gap-6 px-5 lg:px-8">
        <Link to="/" className="group flex items-center gap-3">
          <span
            className={`grid h-11 w-11 place-items-center rounded-full transition-transform duration-300 group-hover:scale-105 ${
              solid ? "bg-ivi-green text-white" : "bg-white/15 text-white ring-1 ring-white/40"
            }`}
          >
            <Leaf className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="leading-tight whitespace-nowrap">
            <span
              className={`block font-heading text-[15px] font-extrabold tracking-tight ${
                solid ? "text-ivi-slate" : "text-white"
              }`}
            >
              Involvement Volunteers
            </span>
            <span
              className={`block text-[11px] font-semibold uppercase tracking-[0.18em] ${
                solid ? "text-ivi-green" : "text-white/75"
              }`}
            >
              International
            </span>
          </span>
        </Link>

        <nav className="ml-auto hidden items-center gap-1 xl:flex">
          {NAV.map((item) => (
            <div key={item.label} className="group relative">
              {item.children ? (
                <button
                  type="button"
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-2 text-[14px] font-semibold transition-colors duration-200 ${
                    solid
                      ? "text-ivi-slate hover:bg-ivi-mint hover:text-ivi-greenDark"
                      : "text-white/90 hover:bg-white/15 hover:text-white"
                  }`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                </button>
              ) : (
                <NavLink item={item} solid={solid} />
              )}
              {item.children && (
                <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 translate-y-2 opacity-0 transition-[opacity,transform] duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="mt-2 overflow-hidden rounded-2xl border border-ivi-mint bg-white p-2 shadow-[0_20px_50px_rgba(24,40,32,0.16)]">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        className="block rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-ivi-slate transition-colors duration-150 hover:bg-ivi-mint hover:text-ivi-greenDark"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 xl:ml-0">
          <button
            type="button"
            aria-label="Search"
            className={`hidden h-10 w-10 place-items-center rounded-full transition-colors duration-200 sm:grid ${
              solid ? "text-ivi-slate hover:bg-ivi-mint" : "text-white hover:bg-white/15"
            }`}
          >
            <Search className="h-[18px] w-[18px]" />
          </button>
          <div className={`hidden items-center gap-1 lg:flex ${solid ? "text-ivi-slate" : "text-white"}`}>
            {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
              <a
                key={i}
                href="#social"
                aria-label="Social link"
                className={`grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 ${
                  solid ? "hover:bg-ivi-mint" : "hover:bg-white/15"
                }`}
              >
                <Icon className="h-[17px] w-[17px]" />
              </a>
            ))}
          </div>
          <a
            href="#apply"
            className="hidden rounded-full bg-ivi-green px-5 py-2.5 text-[14px] font-bold text-white shadow-[0_10px_24px_-8px_rgba(118,188,67,0.9)] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark md:block"
          >
            Apply Now
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className={`grid h-10 w-10 place-items-center rounded-full xl:hidden ${
              solid ? "text-ivi-slate hover:bg-ivi-mint" : "text-white hover:bg-white/15"
            }`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`overflow-hidden border-t border-ivi-mint bg-white transition-[max-height] duration-300 xl:hidden ${
          open ? "max-h-[80vh] overflow-y-auto" : "max-h-0"
        }`}
      >
        <div className="space-y-1 px-5 py-4">
          {NAV.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <>
                  <button
                    type="button"
                    onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left font-heading text-[15px] font-semibold text-ivi-slate hover:bg-ivi-mint"
                  >
                    {item.label}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        openGroup === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openGroup === item.label && (
                    <div className="ml-3 border-l border-ivi-mint pl-3">
                      {item.children.map((c) => (
                        <Link
                          key={c.label}
                          to={c.to}
                          className="block rounded-lg px-3 py-2.5 text-[14px] text-ivi-slate/80 hover:bg-ivi-mint"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <MobileNavLink item={item} />
              )}
            </div>
          ))}
          <a
            href="#apply"
            className="mt-2 block rounded-full bg-ivi-green px-5 py-3 text-center font-bold text-white"
          >
            Apply Now
          </a>
        </div>
      </div>
    </header>
  );
}

/** Hash-aware link: scrolls on the homepage, routes home first from other pages. */
function useHashJump() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (hash) => {
    const id = hash.replace("#", "");
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (pathname === "/") {
      scroll();
    } else {
      navigate("/");
      setTimeout(scroll, 420);
    }
  };
}

function NavLink({ item, solid }) {
  const jump = useHashJump();
  const cls = `flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-2 text-[14px] font-semibold transition-colors duration-200 ${
    solid
      ? "text-ivi-slate hover:bg-ivi-mint hover:text-ivi-greenDark"
      : "text-white/90 hover:bg-white/15 hover:text-white"
  }`;

  if (item.to && item.to.startsWith("#")) {
    return (
      <button type="button" className={cls} onClick={() => jump(item.to)}>
        {item.label}
      </button>
    );
  }
  return (
    <Link to={item.to || "/"} className={cls}>
      {item.label}
    </Link>
  );
}

function MobileNavLink({ item }) {
  const jump = useHashJump();
  const cls =
    "block w-full rounded-xl px-3 py-3 text-left font-heading text-[15px] font-semibold text-ivi-slate hover:bg-ivi-mint";

  if (item.to && item.to.startsWith("#")) {
    return (
      <button type="button" className={cls} onClick={() => jump(item.to)}>
        {item.label}
      </button>
    );
  }
  return (
    <Link to={item.to || "/"} className={cls}>
      {item.label}
    </Link>
  );
}
